/**
 * @module components/assistant/usePanelDrag
 * @description 어시스턴트 패널 위치 이동(드래그) composable. 패널이 우하단 고정(fixed)이므로
 * 앵커(right/bottom)는 유지한 채 오프셋 값만 바꿔 이동한다 — usePanelResize의
 * "핸들을 바깥으로 끌면 커진다" 전제(right/bottom 앵커)가 그대로 성립하도록 좌표계를 유지한다.
 * 이동한 위치는 localStorage에 저장해 새로고침 후에도 유지한다(UI 선호 — 대화 데이터 아님).
 * 헤더 더블클릭 시 기본 위치(우하단)로 복귀한다. PC 리디자인(≥1280px) + 포인터 정밀 기기 전용.
 */
import { ref, computed, watch } from 'vue'
import { useResponsive } from '@/composables/useResponsive'

const STORAGE_KEY = 'aw-panel-pos'
/** 패널 기본 앵커(px) — .aw-panel CSS의 right/bottom 기본값과 동일 */
const DEFAULT_OFFSET = 24
/** 화면 가장자리 최소 여백(px) */
const EDGE_MARGIN = 8
/** 드래그 시작 전(스냅샷 이전) 클램프에 쓰는 PC 패널 기본 폭/높이(px) — .aw-panel--pc CSS와 동일 */
const DEFAULT_PANEL_WIDTH = 420
const DEFAULT_PANEL_HEIGHT = 600
/** usePanelResize가 크기를 저장하는 키 — 복원 클램프의 패널 크기 추정에 읽기 전용으로 참조 */
const SIZE_STORAGE_KEY = 'assistant.panelSize'

/**
 * 패널 위치 이동 상태/핸들러를 제공한다.
 * @param {import('vue').Ref<HTMLElement|null>} [panelEl] - 패널 루트 엘리먼트 — 창 크기 변경 재조정 때 현재 실제 크기를 잰다
 * @returns {{positionStyle:import('vue').ComputedRef<object>, dragHandlers:object, refit:Function}}
 */
export function usePanelDrag(panelEl) {
  const { isPcRedesign, windowWidth, windowHeight } = useResponsive()

  /** 사용자 지정 위치(px 오프셋) — null이면 CSS 기본 우하단 앵커 사용 */
  const pos = ref(null)

  // 진행 중 드래그 상태(반응성 불필요) — { pointerId, x, y, right, bottom, w, h, moved }
  let drag = null
  // 마지막으로 관측한 패널 폭/높이 — 드래그 중이 아닐 때의 창 크기 재클램프에 사용
  let lastPanelWidth = DEFAULT_PANEL_WIDTH
  let lastPanelHeight = DEFAULT_PANEL_HEIGHT

  // 저장된 위치 복원 — 손상 데이터/음수 등 이상 포맷은 무시(pos=null 유지)하고,
  // 유효한 값은 현재 뷰포트로 재클램프한다. 큰 모니터에서 저장한 좌표를 작은 화면에서
  // 그대로 로드하면 화면 밖으로 밀려날 수 있으므로 clampOffset을 거쳐 대입한다.
  // 패널 크기는 아직 DOM이 없어 실측 불가 — usePanelResize 저장 크기(있으면)/기본값으로 추정한다.
  // 세로만 줄어든 환경(clampOffset 내부의 window.innerHeight)도 이 복원 시점 클램프로 함께 커버된다.
  try {
    const size = JSON.parse(localStorage.getItem(SIZE_STORAGE_KEY))
    if (size && Number.isFinite(size.w) && size.w > 0) lastPanelWidth = size.w
    if (size && Number.isFinite(size.h) && size.h > 0) lastPanelHeight = size.h
  } catch (e) { /* 크기 추정 실패 시 기본값 사용 */ }
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY))
    if (saved && Number.isFinite(saved.right) && Number.isFinite(saved.bottom) && saved.right >= 0 && saved.bottom >= 0) {
      pos.value = clampOffset(saved.right, saved.bottom, lastPanelWidth, lastPanelHeight)
    }
  } catch (e) { /* 파싱 실패 시 기본 위치 사용 */ }

  /** 인라인 스타일 — PC가 아니거나 사용자 지정 위치가 없으면 빈 객체(CSS 기본 우하단 유지) */
  const positionStyle = computed(() => (
    !isPcRedesign.value || pos.value == null ? {} : { right: pos.value.right + 'px', bottom: pos.value.bottom + 'px' }
  ))

  /**
   * 우/하 오프셋을 화면 안으로 클램프한다 — 패널 전체(특히 상단의 헤더)가 화면 안에 남도록.
   * ⚠️ 세로 한계는 반드시 패널 높이 기준(top ≥ EDGE_MARGIN)이어야 한다 — "하단 N px 노출"
   * 기준(vh−56)을 쓰면 bottom 오프셋 특성상 화면에 남는 것은 헤더가 아니라 패널 '하단' 띠라,
   * 큰 모니터 좌표 복원 시 헤더가 화면 위로 사라져 복구 불가가 된다(브라우저 실측, AA-116).
   */
  function clampOffset(right, bottom, panelWidth, panelHeight) {
    // 측정 불가(display:none 등 rect 0/비유한값) 크기는 기본값으로 대체 — NaN 전파 방지
    const w = Number.isFinite(panelWidth) && panelWidth > 0 ? panelWidth : DEFAULT_PANEL_WIDTH
    const h = Number.isFinite(panelHeight) && panelHeight > 0 ? panelHeight : DEFAULT_PANEL_HEIGHT
    const maxRight = Math.max(windowWidth.value - w - EDGE_MARGIN, EDGE_MARGIN)
    const maxBottom = Math.max(window.innerHeight - h - EDGE_MARGIN, EDGE_MARGIN)
    return {
      right: Math.min(Math.max(right, EDGE_MARGIN), maxRight),
      bottom: Math.min(Math.max(bottom, EDGE_MARGIN), maxBottom)
    }
  }

  /** 닫기 버튼 등 클릭 가능한 컨트롤 위에서 시작된 포인터는 드래그로 취급하지 않는다 */
  function isControlTarget(e) {
    return !!(e && e.target && e.target.closest && e.target.closest('.aw-close, button'))
  }

  /**
   * 헤더 pointerdown — 드래그 시작. 포인터 캡처로 이후 move/up이 헤더로 전달된다.
   * PC 리디자인 미만/터치 전용 기기(pointer:coarse)/컨트롤 타깃은 무시.
   * @param {PointerEvent} e
   */
  function onPointerDown(e) {
    if (drag || e.button !== 0) return
    if (!isPcRedesign.value) return
    if (typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches) return
    if (isControlTarget(e)) return
    const panel = e.currentTarget.closest('.aw-panel')
    if (!panel) return
    const rect = panel.getBoundingClientRect()
    lastPanelWidth = rect.width
    lastPanelHeight = rect.height
    drag = {
      pointerId: e.pointerId,
      x: e.clientX,
      y: e.clientY,
      right: pos.value ? pos.value.right : DEFAULT_OFFSET,
      bottom: pos.value ? pos.value.bottom : DEFAULT_OFFSET,
      w: lastPanelWidth,
      h: lastPanelHeight,
      moved: false
    }
    try {
      e.currentTarget.setPointerCapture(e.pointerId)
    } catch (err) { /* 포인터가 이미 해제된 경우 등 — 캡처 없이도 헤더 위 드래그는 동작 */ }
    e.preventDefault()
  }

  /**
   * 헤더 pointermove — 시작 오프셋에서 이동 거리만큼 반대로 뺀 값으로 갱신(앵커 불변, 오프셋만 변경).
   * @param {PointerEvent} e
   */
  function onPointerMove(e) {
    if (!drag || e.pointerId !== drag.pointerId) return
    // 포인터 캡처 실패로 pointerup을 놓친 stale 드래그 방어 — 버튼이 떨어져 있으면 종료
    if (e.buttons !== undefined && (e.buttons & 1) === 0) {
      onPointerUp(e)
      return
    }
    drag.moved = true
    const dx = e.clientX - drag.x
    const dy = e.clientY - drag.y
    pos.value = clampOffset(drag.right - dx, drag.bottom - dy, drag.w, drag.h)
  }

  /** 헤더 pointerup/pointercancel — 드래그를 시작한 포인터의, 실제 이동이 있었던 경우만 저장 */
  function onPointerUp(e) {
    if (!drag) return
    if (e && e.pointerId !== undefined && e.pointerId !== drag.pointerId) return
    const moved = drag.moved
    drag = null
    if (!moved) return
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(pos.value))
    } catch (err) { /* 저장 실패는 무시 — 이번 세션 위치는 유지됨 */ }
  }

  /** 헤더 더블클릭 — 기본 위치(우하단) 복귀. 닫기 버튼 등 컨트롤 위 더블클릭은 무시(버블링으로 인한 오인 리셋 방지) */
  function reset(e) {
    if (isControlTarget(e)) return
    drag = null
    pos.value = null
    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch (e) { /* 무시 */ }
  }

  /**
   * 저장된 위치를 현재 창·패널 크기로 다시 클램프한다. 패널 높이는 CSS max-height(100vh − 48px)가 창에 맞춰 줄이므로,
   * 보이는 패널이면 그 실제 크기로 다시 잰다(마지막 드래그 뒤 리사이즈한 크기 포함). 닫힌 패널(크기 0)은 마지막 관측값을 쓰고,
   * 다시 열릴 때 호출측이 한 번 더 부른다(SURINP-842).
   */
  function refit() {
    if (pos.value == null) return
    const el = panelEl && panelEl.value
    if (el && el.offsetHeight > 0) {
      lastPanelWidth = el.offsetWidth
      lastPanelHeight = el.offsetHeight
    }
    pos.value = clampOffset(pos.value.right, pos.value.bottom, lastPanelWidth, lastPanelHeight)
  }

  // 창 폭·높이 어느 쪽이 줄어도 저장된 위치가 화면 밖으로 밀려나지 않도록 재클램프(드래그 중이 아니어도 동작).
  // 높이만 줄면 bottom 오프셋이 그대로라 헤더가 화면 위로 사라졌다(SURINP-842). 렌더 뒤(post)에 재야
  // 폭이 PC 경계를 넘을 때 바뀐 크기(.aw-panel--pc)를 읽는다 — 같은 flush 안이라 화면에 어긋난 프레임은 그려지지 않는다.
  watch([windowWidth, windowHeight], refit, { flush: 'post' })

  /** 헤더에 v-on 일괄 바인딩용 — capture는 down 발생 엘리먼트로 라우팅되므로 5종 전부 동일 엘리먼트 필요 */
  const dragHandlers = {
    pointerdown: onPointerDown,
    pointermove: onPointerMove,
    pointerup: onPointerUp,
    pointercancel: onPointerUp,
    dblclick: reset
  }

  return { positionStyle, dragHandlers, refit }
}
